const PLUS_CODE_PREFIX_REGEX =
  /^\s*[23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,}(?:\s+[23456789CFGHJMPQRVWX]{2,})?\s*,\s*/i;

const TRAILING_COUNTRY_REGEX = /,\s*India\s*$/i;
const LEADING_NUMBER_SEGMENT_REGEX = /^\s*\d+[A-Za-z/-]*\s*,\s*/;

const RESULT_TYPE_PRIORITY = [
  "street_address",
  "premise",
  "subpremise",
  "route",
  "intersection",
  "neighborhood",
  "sublocality_level_1",
  "sublocality",
  "locality",
];

const normalizeSegment = (value) => String(value || "").replace(/\s+/g, " ").trim();

const uniqueSegments = (segments) => {
  const seen = new Set();

  return segments.filter((segment) => {
    const normalized = normalizeSegment(segment);

    if (!normalized) {
      return false;
    }

    const key = normalized.toLowerCase();
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const getAddressComponent = (components, types) => {
  const typeList = Array.isArray(types) ? types : [types];

  return (
    components.find((component) =>
      typeList.some((type) => component?.types?.includes(type))
    )?.long_name || ""
  );
};

export const cleanDisplayAddress = (address) =>
  normalizeSegment(address)
    .replace(PLUS_CODE_PREFIX_REGEX, "")
    .replace(TRAILING_COUNTRY_REGEX, "");

const stripLeadingNumberSegment = (address) =>
  cleanDisplayAddress(address).replace(LEADING_NUMBER_SEGMENT_REGEX, "");

export const getBestGeocodeResult = (results = []) => {
  if (!Array.isArray(results) || results.length === 0) {
    return null;
  }

  return [...results].sort((left, right) => {
    const leftPriority = RESULT_TYPE_PRIORITY.findIndex((type) =>
      left?.types?.includes(type)
    );
    const rightPriority = RESULT_TYPE_PRIORITY.findIndex((type) =>
      right?.types?.includes(type)
    );

    const normalizedLeftPriority =
      leftPriority === -1 ? RESULT_TYPE_PRIORITY.length : leftPriority;
    const normalizedRightPriority =
      rightPriority === -1 ? RESULT_TYPE_PRIORITY.length : rightPriority;

    return normalizedLeftPriority - normalizedRightPriority;
  })[0];
};

export const formatAddressFromGeocodeResult = (result, fallback = "") => {
  if (!result) {
    return cleanDisplayAddress(fallback);
  }

  const components = Array.isArray(result.address_components)
    ? result.address_components
    : [];

  const subpremise = getAddressComponent(components, "subpremise");
  const premise = getAddressComponent(components, "premise");
  const streetNumber = getAddressComponent(components, "street_number");
  const route = getAddressComponent(components, "route");
  const neighborhood = getAddressComponent(components, "neighborhood");
  const sublocality2 = getAddressComponent(components, "sublocality_level_2");
  const sublocality1 = getAddressComponent(components, "sublocality_level_1");
  const sublocality = getAddressComponent(components, "sublocality");
  const locality = getAddressComponent(components, "locality");
  const adminArea2 = getAddressComponent(components, "administrative_area_level_2");
  const state = getAddressComponent(components, "administrative_area_level_1");
  const postalCode = getAddressComponent(components, "postal_code");

  const buildingLine = uniqueSegments([subpremise, premise]).join(", ");
  const streetLine = streetNumber && route
    ? `${streetNumber}, ${route}`
    : uniqueSegments([streetNumber, route]).join(", ");
  const city = locality || adminArea2;
  const stateAndPostal = uniqueSegments(
    [`${state}${postalCode ? ` ${postalCode}` : ""}`]
  ).join(", ");

  const assembledAddress = uniqueSegments([
    buildingLine,
    streetLine,
    neighborhood,
    sublocality2,
    sublocality1,
    sublocality,
    city,
    stateAndPostal,
  ]).join(", ");

  return cleanDisplayAddress(
    assembledAddress || result.formatted_address || fallback
  );
};

export const getDisplayAddressFromGeocodeResults = (results, fallback = "") =>
  formatAddressFromGeocodeResult(getBestGeocodeResult(results), fallback);

export const formatCurrentLocationAddressFromGeocodeResult = (
  result,
  fallback = ""
) => {
  if (!result) {
    return stripLeadingNumberSegment(fallback);
  }

  const components = Array.isArray(result.address_components)
    ? result.address_components
    : [];

  const route = getAddressComponent(components, "route");
  const neighborhood = getAddressComponent(components, "neighborhood");
  const sublocality2 = getAddressComponent(components, "sublocality_level_2");
  const sublocality1 = getAddressComponent(components, "sublocality_level_1");
  const sublocality = getAddressComponent(components, "sublocality");
  const locality = getAddressComponent(components, "locality");
  const adminArea2 = getAddressComponent(
    components,
    "administrative_area_level_2"
  );
  const state = getAddressComponent(components, "administrative_area_level_1");
  const postalCode = getAddressComponent(components, "postal_code");

  const city = locality || adminArea2;
  const stateAndPostal = uniqueSegments([
    `${state}${postalCode ? ` ${postalCode}` : ""}`,
  ]).join(", ");

  const assembledAddress = uniqueSegments([
    route,
    neighborhood,
    sublocality2,
    sublocality1,
    sublocality,
    city,
    stateAndPostal,
  ]).join(", ");

  return stripLeadingNumberSegment(
    assembledAddress || result.formatted_address || fallback
  );
};

export const getCurrentLocationAddressFromGeocodeResults = (
  results,
  fallback = ""
) =>
  formatCurrentLocationAddressFromGeocodeResult(
    getBestGeocodeResult(results),
    fallback
  );
